import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { WatchListRow } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { SectionHeader } from "../SectionHeader";
import { ContractLaserDeductibleChip, LaseredDeductibleChip } from "@/components/ui/mui/LaseredDeductibleChip";
import { LaserChipWithReview } from "@/components/common/DeductibleReviewFlag";
import { getColumnLabel } from "./shared/claimsSummaryColumnsHelper";

// ==================== TYPES ====================

interface WatchListTableProps {
  rows: WatchListRow[];
  loading: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

// ==================== THRESHOLD CHIP ====================

const thresholdColors: Record<string, "error" | "warning" | "info"> = {
  Approaching: "error",
  "75% Notice": "warning",
  "50% Notice": "info",
};

const ThresholdChip = ({ threshold }: { threshold: string }) => (
  <Chip
    label={threshold}
    color={thresholdColors[threshold] ?? "default"}
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
  />
);

// ==================== COLUMNS ====================

const columns: CompactTableColumn<WatchListRow>[] = [
  {
    key: "claimant",
    label: getColumnLabel("claimant"),
    width: "1.5fr",
    render: (row) => (
      // The name shrinks and wraps; the flag does not (it overprinted the next
      // column's amount at laptop widths, UX review #1).
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Link
          to={`/claimants/${row.id}/show`}
          onClick={(e) => e.stopPropagation()}
          style={{ textDecoration: "none", minWidth: 0 }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              textDecoration: "underline",
              fontWeight: 500,
              overflowWrap: "anywhere",
              "&:hover": { color: "primary.dark" },
            }}
          >
            {row.name}
          </Typography>
        </Link>
        {/* A contract laser (#5287) and a straight laser both give the claimant an
            individual deductible, so both are indicated -- but with the distinct chip
            each already has. Before #6929 a contract-lasered claimant got NEITHER,
            because the row flag was read straight off `is_lasered`. */}
        <LaserChipWithReview
          pending={row.isDeductibleReviewPending}
          claimantId={row.id}
          policyId={row.policyId}
          claimantName={row.name}
        >
          {row.isContractLasered ? <ContractLaserDeductibleChip /> : row.isLasered && <LaseredDeductibleChip />}
        </LaserChipWithReview>
      </Box>
    ),
  },
  {
    key: "memberId",
    label: getColumnLabel("memberId"),
    width: "1fr",
    render: (row) => (
      <Typography variant="body2" color="text.secondary">
        {row.memberId || "—"}
      </Typography>
    ),
  },
  {
    key: "totalYTD",
    label: getColumnLabel("watchListTotalYTD"),
    width: "120px",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatUSD(row.totalYTD)}
      </Typography>
    ),
  },
  {
    key: "deductible",
    label: getColumnLabel("deductible"),
    width: "120px",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatUSD(row.specDeductible)}
      </Typography>
    ),
  },
  {
    key: "percentOfDeductible",
    label: getColumnLabel("percentOfDeductible"),
    width: "100px",
    align: "right",
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          color:
            row.percentOfDeductible >= 90 ? "error.main" : row.percentOfDeductible >= 75 ? "warning.main" : "info.main",
        }}
      >
        {row.percentOfDeductible.toFixed(1)}%
      </Typography>
    ),
  },
  {
    key: "remaining",
    label: getColumnLabel("remaining"),
    width: "120px",
    align: "right",
    render: (row) => (
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatUSD(row.remaining)}
      </Typography>
    ),
  },
  {
    key: "threshold",
    label: getColumnLabel("threshold"),
    width: "120px",
    render: (row) => <ThresholdChip threshold={row.threshold} />,
  },
];

// ==================== MAIN COMPONENT ====================

export const WatchListTable = ({ rows, loading, isExpanded, onToggle }: WatchListTableProps) => {
  return (
    <SectionHeader
      title="Watch List"
      subtitle="Claimants between 50–99% of their deductible"
      count={!loading ? rows.length : undefined}
      collapsible
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <CompactTable
          data={rows}
          columns={columns}
          rowsPerPage={10}
          emptyMessage="No claimants are currently between 50-99% of their deductible for the selected date range."
        />
      )}
    </SectionHeader>
  );
};
